// Global Instructions Rule Applied! - 2026-04-21
import {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type MutableRefObject,
  type ReactNode,
  type Ref
} from 'react'
import { createPortal } from 'react-dom'
import DOMPurify from 'dompurify'

export interface ShadowRichHtmlProps {
  html: unknown
  className?: string
  /**
   * Component-authored CSS for the shadow root. Injected as a real <style>
   * node BEFORE the sanitized content, so it never passes through DOMPurify —
   * it is developer code, not the untrusted string in `html`. Needed because
   * the shadow root is style-isolated: app CSS cannot reach inside it, so a
   * surface that renders prose (rather than already-styled email HTML) has to
   * bring its own typography.
   */
  css?: string
}

export function ShadowRichHtml({ html, className, css }: ShadowRichHtmlProps) {
  const hostRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!hostRef.current) return

    const shadowRoot =
      hostRef.current.shadowRoot || hostRef.current.attachShadow({ mode: 'open' })

    // Client-side DOMPurify at the render sink (plan §7.7). Shadow DOM is style
    // isolation, not a security boundary — onerror/onload/javascript: would run
    // in the viewer's authenticated session if anything slipped past the server
    // sanitizer. One extra defence layer isolating every preview surface.
    shadowRoot.innerHTML = typeof html === 'string' ? DOMPurify.sanitize(html) : ''

    if (css) {
      const styleEl = document.createElement('style')
      styleEl.textContent = css
      shadowRoot.insertBefore(styleEl, shadowRoot.firstChild)
    }
  }, [html, css])

  return <div ref={hostRef} className={className} />
}

export interface ShadowPortalProps {
  /** Light-DOM host; use for layout (e.g. Tailwind height/flex). */
  className?: string
  style?: CSSProperties
  tabIndex?: number
  /** Injected as `<link rel="stylesheet">` inside the shadow root, in order. */
  stylesheetHrefs?: string[]
  /** Optional class on the shadow mount node (portal target). */
  mountClassName?: string
  children: ReactNode
  /** Fires once when the shadow root is created (e.g. pass to Jodit `shadowRoot` option). */
  onShadowRootReady?: (root: ShadowRoot) => void
}

const NO_STYLESHEETS: string[] = []

const DEFAULT_MOUNT_STYLE =
  'display:flex;flex-direction:column;height:100%;min-height:0;flex:1;overflow:hidden;box-sizing:border-box'

function mergeRefs<T>(...refs: Array<Ref<T> | undefined>) {
  return (node: T | null) => {
    for (const r of refs) {
      if (!r) continue
      if (typeof r === 'function') r(node)
      else (r as MutableRefObject<T | null>).current = node
    }
  }
}

/**
 * Opens a shadow root on the host, optionally loads stylesheets, then renders `children` via portal.
 * Use for live React UI (e.g. Jodit) that must stay style-isolated; use {@link ShadowRichHtml} for static HTML strings.
 */
export const ShadowPortal = forwardRef<HTMLDivElement, ShadowPortalProps>(function ShadowPortal(
  {
    className,
    style,
    tabIndex,
    stylesheetHrefs = NO_STYLESHEETS,
    mountClassName,
    children,
    onShadowRootReady
  },
  ref
) {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const [mountEl, setMountEl] = useState<HTMLDivElement | null>(null)
  const onReadyRef = useRef(onShadowRootReady)
  onReadyRef.current = onShadowRootReady

  const hrefKey = stylesheetHrefs.join('|')

  useLayoutEffect(() => {
    const host = hostRef.current
    if (!host) return

    if (host.shadowRoot) return

    const sr = host.attachShadow({ mode: 'open' })
    for (const href of stylesheetHrefs) {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = href
      sr.appendChild(link)
    }
    const mount = document.createElement('div')
    if (mountClassName) mount.className = mountClassName
    mount.style.cssText = DEFAULT_MOUNT_STYLE
    sr.appendChild(mount)
    setMountEl(mount)
    onReadyRef.current?.(sr)
  }, [hrefKey, mountClassName, stylesheetHrefs])

  return (
    <div ref={mergeRefs(hostRef, ref)} className={className} style={style} tabIndex={tabIndex}>
      {mountEl ? createPortal(children, mountEl) : null}
    </div>
  )
})

ShadowPortal.displayName = 'ShadowPortal'
